import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Button, Typography, Box, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Switch, FormControlLabel, Alert, Autocomplete, Chip
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import api from '../services/api';

function ParteExterna() {
    const navigate = useNavigate();
    const [parteExternaList, setParteExternaList] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ nome_completo: '', cliente_id: '', observacao: '', ativo: true });
    const [error, setError] = useState('');

    useEffect(() => {
        loadParteExternaList();
        loadClientes();
    }, []);

    const loadParteExternaList = async () => {
        setLoading(true);
        try {
            const response = await api.get('/parte-externa');
            setParteExternaList(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar lista de partes externas:', error);
            setError('Erro ao carregar lista de partes externas');
        } finally {
            setLoading(false);
        }
    };

    const loadClientes = async () => {
        try {
            const response = await api.get('/clientes');
            setClientes(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar clientes:', error);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({ 
                nome_completo: item.nome_completo || '', 
                cliente_id: item.cliente_id || '', 
                observacao: item.observacao || '',
                ativo: item.ativo === 1
            });
        } else {
            setEditingItem(null);
            setFormData({ nome_completo: '', cliente_id: '', observacao: '', ativo: true });
        }
        setOpenDialog(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ nome_completo: '', cliente_id: '', observacao: '', ativo: true });
        setError('');
    };

    const handleSave = async () => {
        if (!formData.nome_completo.trim()) {
            setError('Nome completo é obrigatório');
            return;
        }

        try {
            if (editingItem) {
                await api.put(`/parte-externa/${editingItem.id}`, formData);
            } else {
                await api.post('/parte-externa', formData);
            }
            handleCloseDialog();
            loadParteExternaList();
        } catch (error) {
            console.error('Erro ao salvar parte externa:', error);
            setError(error.response?.data?.error || 'Erro ao salvar parte externa');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir esta parte externa?')) {
            try {
                await api.delete(`/parte-externa/${id}`);
                loadParteExternaList();
            } catch (error) {
                console.error('Erro ao deletar parte externa:', error);
                alert('Erro ao deletar parte externa: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const parteExternaFiltradas = parteExternaList.filter(p => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            p.nome_completo?.toLowerCase().includes(searchLower) ||
            p.cliente_nome?.toLowerCase().includes(searchLower) ||
            p.observacao?.toLowerCase().includes(searchLower)
        );
    });

    return (
        <Container>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" component="h1">
                    Gerenciar Partes Externas
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Nova Parte Externa
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Buscar por nome, cliente ou observação..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome Completo</TableCell>
                            <TableCell>Cliente Vinculado</TableCell>
                            <TableCell>Observação</TableCell>
                            <TableCell align="center">Ativo</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Carregando...</TableCell>
                            </TableRow>
                        ) : parteExternaFiltradas.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    {search ? 'Nenhuma parte externa encontrada' : 'Nenhuma parte externa cadastrada'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            parteExternaFiltradas.map((parte) => (
                                <TableRow key={parte.id} hover>
                                    <TableCell>{parte.nome_completo}</TableCell>
                                    <TableCell>
                                        {parte.cliente_nome ? (
                                            <Chip 
                                                label={`${parte.cliente_nome}${parte.cliente_cpf ? ' - ' + parte.cliente_cpf : ''}`} 
                                                size="small" 
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">Não vinculado</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{parte.observacao || '-'}</TableCell>
                                    <TableCell align="center">
                                        {parte.ativo === 1 ? '✓' : '✗'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={() => handleOpenDialog(parte)}
                                            color="primary"
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(parte.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingItem ? 'Editar Parte Externa' : 'Nova Parte Externa'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Nome Completo"
                            value={formData.nome_completo}
                            onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                            margin="normal"
                            required
                        />
                        <Autocomplete
                            options={clientes}
                            getOptionLabel={(option) => `${option.nome}${option.cpf ? ' - ' + option.cpf : ''}`}
                            value={clientes.find(c => c.id === parseInt(formData.cliente_id)) || null}
                            onChange={(e, newValue) => {
                                setFormData({ ...formData, cliente_id: newValue ? newValue.id : '' });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Vincular a Cliente (Opcional)"
                                    margin="normal"
                                    helperText="Vincule a um cliente existente para padronizar os cadastros"
                                />
                            )}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Observação"
                            value={formData.observacao}
                            onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
                            margin="normal"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.ativo}
                                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                                />
                            }
                            label="Ativo"
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default ParteExterna;

